import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FiDownload, FiSmartphone, FiX } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';

import usePWAInstall from './usePWAInstall';
import PWAInstallModal from './PWAInstallModal';

const DISMISS_KEY = 'yoda-pwa-prompt-dismissed';
const DISMISS_DURATION = 30 * 24 * 60 * 60 * 1000;

const PUBLIC_PATHS = [
  '/',
  '/about',
  '/training',
  '/resources',
  '/news',
  '/contact-us',
];

const PWAInstallPrompt = () => {
  console.log('PWA Debug:', {
    pathname: window.location.pathname,
    standalone: window.matchMedia('(display-mode: standalone)').matches,
    iosStandalone: window.navigator.standalone,
    dismissedAt: localStorage.getItem('yoda-pwa-prompt-dismissed'),
  });
  const { pathname } = useLocation();
  const { canInstall, installed, install } = usePWAInstall();

  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const isPublicPage = PUBLIC_PATHS.some(
    (path) =>
      pathname === path || (path !== '/' && pathname.startsWith(`${path}/`))
  );

  useEffect(() => {
    if (!isPublicPage || installed) {
      setVisible(false);
      return;
    }

    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);

    if (dismissedAt && Date.now() - dismissedAt < DISMISS_DURATION) {
      return;
    }

    const timer = setTimeout(() => {
      setVisible(true);
    }, 15000);

    return () => clearTimeout(timer);
  }, [isPublicPage, installed]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const handleInstall = async () => {
    setVisible(false);

    if (canInstall) {
      const accepted = await install();

      if (!accepted) {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      }
    } else {
      setModalOpen(true);
    }
  };

  if (installed) return null;

  return (
    <>
      {visible && isPublicPage && (
        <PromptContainer role="region" aria-label="Install YODA">
          <CloseButton onClick={dismiss} aria-label="Dismiss">
            <FiX />
          </CloseButton>

          <PromptHeader>
            <PromptIcon>
              <FiSmartphone />
            </PromptIcon>

            <PromptText>
              <h3>Take YODA with you</h3>
              <p>
                Install YODA for quick access to your training, courses, and
                certificates.
              </p>
            </PromptText>
          </PromptHeader>

          <PromptActions>
            <SecondaryButton onClick={dismiss}>Not now</SecondaryButton>

            <PrimaryButton onClick={handleInstall}>
              <FiDownload />
              Install YODA
            </PrimaryButton>
          </PromptActions>
        </PromptContainer>
      )}

      <PWAInstallModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onInstall={handleInstall}
        canInstall={canInstall}
      />
    </>
  );
};

export default PWAInstallPrompt;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PromptContainer = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9998;

  width: min(420px, calc(100vw - 32px));
  padding: 24px;

  background: #fff;
  color: #171717;
  border: 1px solid #e7e7e7;
  border-radius: 16px;

  box-shadow: 0 15px 60px rgba(0, 0, 0, 0.2);

  animation: ${slideIn} 0.35s ease-out;

  @media (max-width: 600px) {
    right: 16px;
    bottom: calc(16px + env(safe-area-inset-bottom));
    padding: 20px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;

  width: 30px;
  height: 30px;

  display: grid;
  place-items: center;

  border: none;
  border-radius: 50%;
  background: transparent;
  color: #666;
  cursor: pointer;

  &:hover {
    background: #f1f1f1;
  }
`;

const PromptHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding-right: 12px;
`;

const PromptIcon = styled.div`
  width: 48px;
  height: 48px;
  flex-shrink: 0;

  display: grid;
  place-items: center;

  background: #fff3cc;
  border-radius: 12px;

  color: #171717;
  font-size: 23px;
`;

const PromptText = styled.div`
  h3 {
    margin: 0 0 7px;
    font-size: 1.05rem;
    font-weight: 800;
  }

  p {
    margin: 0;
    color: #666;
    font-size: 0.88rem;
    line-height: 1.6;
  }
`;

const PromptActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const SecondaryButton = styled.button`
  padding: 11px 15px;
  border: none;
  border-radius: 8px;
  background: #f1f1f1;
  color: #333;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  padding: 11px 16px;
  border: none;
  border-radius: 8px;

  background: var(--yellow4, #f4c542);
  color: #111;

  font-size: 0.85rem;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    filter: brightness(0.95);
  }
`;
